/**
 * How the kiosk looks beyond the layout: the theme and its colour scheme, the look of each module and
 * widget, chat alignment and size, the margin around the screen, a few switches. In layers, like the
 * layout — the last one wins:
 *
 *   defaults → the theme's `appearance` → the preset's `appearance` → Settings (saved on this device)
 *
 * The settings are a device's preference, not the session's: they live in localStorage, and a page
 * that cannot use it (a private window, a sandboxed view) simply starts from the preset.
 */
import { z } from 'zod';
import { report } from './diagnostics';

export const appearanceSchema = z.object({
  /** Light or dark: every theme has both palettes (layout/themes.ts). */
  scheme: z.enum(['light', 'dark']),
  /** `side`: the old kiosk's chat (user left, assistant set in). `center`: one centred column. */
  chatAlign: z.enum(['side', 'center']),
  /** Multiplies the chat text (1 = 24 px bubbles). */
  chatScale: z.number().min(0.8).max(1.8),
  /** Widest a bubble may be, in canvas px. */
  chatWidth: z.number().min(320).max(1080),
  /** Margin between the screen edges and what is not an edge-to-edge bar (chat, content…), in canvas px. */
  framePadding: z.number().min(0).max(80),
  /** Space inside a card (Panel), between its border and its title and content, in canvas px. */
  panelPadding: z.number().min(8).max(48),
  /** Which face the assistant has (avatar/face.ts): `auto` = what the scene or module asks for. */
  face: z.enum(['auto', 'musetalk', 'lowpoly', 'idle', 'particles']),
  /** The stage's background (stage/catalog.ts): `auto` = the pack's, `none` = the theme's plain one, or a catalog id. */
  background: z.string(),
  /** What shows where MuseTalk is wanted but not there: a theme's choice (Classico: idle, Essenziale: orb). */
  faceFallback: z.enum(['idle', 'particles']),
  /** The frame that marks a screen that is not the user's own (replay, operator): a border, a fade, or none (the tag stays). */
  roleFrame: z.enum(['solid', 'fade', 'off']),
  /** How each turn was produced, under the answers (dev). */
  turnDetails: z.boolean(),
  /** Layout and screen animations. */
  motion: z.boolean(),
  /** The on-screen keyboard button next to the text box. */
  keyboardButton: z.boolean(),
  /** Where the chat's keyboard opens: on the canvas's bottom edge, full width, or small, above the text box (07). */
  keyboardPlace: z.enum(['bottom', 'box']),
  /** Touches shown in the chat, as the user's bubbles with a hand ("Cerca treni per Milano"). */
  touchPills: z.boolean(),
  /** Which look each screen's widget uses, by screen (Settings → Screens, docs/NEW-UI/07 D-04): before widgetVariants. */
  screenVariants: z.record(z.string(), z.string()),
  /** Which look each widget uses, by widget key: `{ counters: 'cards' }`. */
  widgetVariants: z.record(z.string(), z.string()),
  /** The looks preferred for any widget that has one, in order (a theme's: `['single', 'card']`), after widgetVariants. */
  preferredVariants: z.array(z.string()),
  /** Which look each module uses, by module id: `{ chat: 'lyrics', 'main-bar': 'pills' }`. */
  moduleVariants: z.record(z.string(), z.string()),
  /** Lyrics chat: how many earlier assistant lines stay visible above the current one. */
  lyricsHistory: z.number().int().min(0).max(3),
  /** Lyrics chat: light the words up while the voice says them (estimated from the audio played). */
  karaoke: z.boolean(),
  /** The replies are spoken with the microphone off too, after a touch or typed words (`?speak=1` forces it). */
  speakTyped: z.boolean(),
  /** A card's detail a reply opened (core/detail.ts) closes by itself this long after it opened or was touched; 0: never. */
  detailSeconds: z.number().int().min(0).max(120),
}).strict();

export type Appearance = z.infer<typeof appearanceSchema>;

export const DEFAULT_APPEARANCE: Appearance = {
  scheme: 'light',
  chatAlign: 'side',
  chatScale: 1,
  chatWidth: 440,
  framePadding: 0,
  panelPadding: 16,
  roleFrame: 'fade',
  face: 'auto',
  background: 'auto',
  faceFallback: 'idle',
  turnDetails: false,
  motion: true,
  keyboardButton: true,
  keyboardPlace: 'bottom',
  touchPills: true,
  screenVariants: {},
  widgetVariants: {},
  preferredVariants: [],
  moduleVariants: {},
  lyricsHistory: 1,
  karaoke: true,
  speakTyped: false,
  detailSeconds: 10,
};

/** The choices offered in Settings, so the panel and the tests share them. */
export const CHAT_SCALES = [1, 1.15, 1.3, 1.5] as const;
export const CHAT_WIDTHS = [440, 640, 820, 960] as const;
export const FRAME_PADDINGS = [0, 16, 32, 48] as const;
export const PANEL_PADDINGS = [12, 16, 24, 32] as const;
export const ROLE_FRAMES = ['solid', 'fade', 'off'] as const;
export const LYRICS_HISTORY = [0, 1, 2, 3] as const;
/** 0: the detail stays open until closed. */
export const DETAIL_SECONDS = [10, 20, 30, 0] as const;

/** Device settings that are not part of the look: the theme and the preset chosen. */
export interface DeviceSettings extends Partial<Appearance> {
  theme?: string;
  preset?: string;
}

export const partialAppearanceSchema = appearanceSchema.partial();

export function resolveAppearance(...layers: (Partial<Appearance> | undefined)[]): Appearance {
  const out: Appearance = { ...DEFAULT_APPEARANCE, screenVariants: {}, widgetVariants: {}, moduleVariants: {} };
  for (const layer of layers) {
    if (!layer) continue;
    const { screenVariants, widgetVariants, moduleVariants, ...rest } = layer;
    Object.assign(out, rest);
    // variant choices merge one by one, not wholesale
    if (screenVariants) out.screenVariants = { ...out.screenVariants, ...screenVariants };
    if (widgetVariants) out.widgetVariants = { ...out.widgetVariants, ...widgetVariants };
    if (moduleVariants) out.moduleVariants = { ...out.moduleVariants, ...moduleVariants };
  }
  return out;
}

const STORAGE_KEY = 'kiosk-ui.settings.v1';
const settingsSchema = partialAppearanceSchema.extend({ theme: z.string().optional(), preset: z.string().optional() });

/** What Settings saved on this device; nothing (and a note) if it cannot be read. */
export function loadSettings(storage: Pick<Storage, 'getItem'> | null = safeStorage()): DeviceSettings {
  if (!storage) return {};
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = settingsSchema.safeParse(JSON.parse(raw));
    if (parsed.success) return parsed.data;
    report('warn', 'settings', 'saved settings are not valid: starting from the preset', parsed.error.issues);
  } catch (e) {
    report('warn', 'settings', 'saved settings cannot be read', e);
  }
  return {};
}

export function saveSettings(settings: DeviceSettings, storage: Pick<Storage, 'setItem' | 'removeItem'> | null = safeStorage()): void {
  if (!storage) return;
  try {
    if (Object.keys(settings).length === 0) storage.removeItem(STORAGE_KEY);
    else storage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (e) {
    report('warn', 'settings', 'settings cannot be saved on this device', e);
  }
}

function safeStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null; // storage blocked (sandboxed or private view): settings last until reload
  }
}
